import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { StepCard } from "./report-step-card";
import { useUpdateReportStep } from "../hooks/reportstep/use-reportstep-update";
import { useDeleteReportStep } from "../hooks/reportstep/use-reportstep-delete";
import type { UiReportStepRow } from "../../../queries/reports";

interface ReportStepsListProps {
  steps: UiReportStepRow[] | undefined;
  editingId: string | null;
  draft: string;
  startingId: string | null;
  isReadOnly?: boolean;
  executionUnavailable?: boolean;
  onEdit: (step: UiReportStepRow) => void;
  onSave: (step: UiReportStepRow) => void;
  onCancel: () => void;
  onDraftChange: (draft: string) => void;
  onStart: (stepId: string) => void;
  onDelete: (step: UiReportStepRow) => void;
}

function EmptyStepsCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">No steps yet</CardTitle>
        <CardDescription>This report does not have any steps.</CardDescription>
      </CardHeader>
    </Card>
  );
}

export function ReportStepsList({
  steps,
  editingId,
  draft,
  startingId,
  isReadOnly = false,
  executionUnavailable = false,
  onEdit,
  onSave,
  onCancel,
  onDraftChange,
  onStart,
  onDelete,
}: ReportStepsListProps) {
  const updateStep = useUpdateReportStep();
  const deleteStep = useDeleteReportStep();

  if (!steps?.length) {
    return <EmptyStepsCard />;
  }

  return (
    <div className="flex flex-col gap-4">
      {steps.map((step) => {
        const isEditing = editingId === step.analysis_step_id;
        const isSaving =
          updateStep.isPending && editingId === step.analysis_step_id;
        const saveError =
          updateStep.isError && isEditing
            ? updateStep.error instanceof Error
              ? updateStep.error.message
              : "Failed to save step"
            : null;
        const isDeleting =
          deleteStep.isPending &&
          deleteStep.variables?.stepId === step.analysis_step_id;

        return (
          <StepCard
            key={step.analysis_step_id}
            step={step}
            isEditing={isEditing}
            isSaving={isSaving}
            draft={draft}
            saveError={saveError}
            isStarting={startingId === step.analysis_step_id}
            isDeleting={isDeleting}
            isReadOnly={isReadOnly}
            executionUnavailable={executionUnavailable}
            onEdit={() => {
              if (!isReadOnly) onEdit(step);
            }}
            onSave={() => {
              if (!isReadOnly) onSave(step);
            }}
            onCancel={onCancel}
            onDraftChange={onDraftChange}
            onStart={() => {
              if (!isReadOnly && !executionUnavailable)
                onStart(step.analysis_step_id);
            }}
            onDelete={() => {
              if (!isReadOnly) onDelete(step);
            }}
          />
        );
      })}
    </div>
  );
}
